import { Link } from "react-router-dom";
import { useCountUp } from "@/hooks/useCountUp";

const METRICS = [
  { label: "Verticals", target: 3, suffix: "" },
  { label: "Countries", target: 3, suffix: "" },
  { label: "Pipeline cases", target: 200, suffix: "+" },
  { label: "Beta clients", target: 15, suffix: "" },
];

function MetricCell({ label, target, suffix }: { label: string; target: number; suffix: string }) {
  const { value, ref } = useCountUp(target);
  return (
    <div ref={ref as React.RefObject<HTMLDivElement>} className="bg-[var(--color-bg)] p-6">
      <div className="text-[10px] text-[var(--color-fg-tertiary)] font-[family-name:var(--font-mono)] tracking-widest uppercase mb-2">
        {label}
      </div>
      <span className="text-2xl md:text-3xl font-light tabular-nums font-[family-name:var(--font-mono)]">
        {value}{suffix}
      </span>
    </div>
  );
}

export function HeroSection({ scrollY }: { scrollY: React.RefObject<number> }) {
  return (
    <section
      id="hero"
      className="relative min-h-screen short:min-h-0 short:py-24 flex flex-col justify-center items-center px-8 z-10"
    >
      <div
        className="text-center max-w-4xl"
        style={{
          transform: `translateY(${(scrollY.current ?? 0) * -0.15}px)`,
        }}
      >
        <h1 className="text-5xl md:text-[clamp(3.75rem,min(5.5vw,8vh),6rem)] tracking-tight leading-[0.95] mb-[clamp(1.5rem,3vh,2rem)] font-[family-name:var(--font-display)]">
          <span className="font-light">We build AI agents</span>
          <br />
          <span className="font-light">for </span>
          <span className="font-semibold">regulated industries</span>
        </h1>
        <p className="text-lg md:text-[clamp(1.125rem,1.5vw,1.25rem)] text-[var(--color-fg-secondary)] max-w-2xl mx-auto leading-relaxed font-light mb-10">
          COSX deploys AI agents that handle real casework in immigration law, education, and financial services. Built for compliance, auditability, and human oversight.
        </p>
        <div className="flex flex-wrap justify-center gap-4 mb-[clamp(2rem,5vh,4rem)]">
          <Link
            to="/contact"
            className="inline-flex items-center px-6 py-3 rounded-full text-sm font-medium bg-[var(--color-fg)] text-[var(--color-bg)] hover:opacity-90 transition-opacity"
          >
            Get in touch
          </Link>
          <Link
            to="/products"
            className="inline-flex items-center px-6 py-3 rounded-full text-sm font-medium border border-[var(--color-border)] text-[var(--color-fg-secondary)] hover:text-[var(--color-fg)] hover:border-[var(--color-fg-secondary)] transition-colors"
          >
            See our work →
          </Link>
        </div>

        {/* Metrics bar */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[var(--color-border)] max-w-3xl mx-auto">
          {METRICS.map((m) => (
            <MetricCell key={m.label} {...m} />
          ))}
        </div>
      </div>

      {/* Scroll indicator */}
      <div
        className="absolute bottom-12 flex flex-col items-center gap-2 text-[var(--color-fg-tertiary)]"
        style={{ opacity: Math.max(0, 1 - (scrollY.current ?? 0) / 200) }}
      >
        <span className="text-xs font-[family-name:var(--font-mono)] tracking-widest uppercase">
          Scroll
        </span>
        <div className="relative w-px h-8 bg-[var(--color-fg-tertiary)] opacity-30 overflow-hidden">
          <div
            className="absolute left-0 w-full h-2 animate-[scrollDot_2s_ease-in-out_infinite]"
            style={{
              background: "linear-gradient(to bottom, transparent, var(--color-fg), transparent)",
            }}
          />
        </div>
      </div>
    </section>
  );
}
